import React, { useState } from 'react';
import { Modal, Form, Input, Button, message, Steps, Typography } from 'antd';
import { MailOutlined, LockOutlined } from '@ant-design/icons';
import { authService } from '../../services/authService';
import { useTranslation, Trans } from 'react-i18next';

const { Step } = Steps;
const { Text } = Typography;

interface ResetPasswordModalProps {
  visible: boolean;
  onCancel: () => void;
}

const ResetPasswordModal: React.FC<ResetPasswordModalProps> = ({ visible, onCancel }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [email, setEmail] = useState('');

  const handleInitReset = async (values: { email: string }) => {
    try {
      setLoading(true);
      await authService.resetPasswordInit(values.email);
      setEmail(values.email);
      setCurrentStep(1);
      message.success(t('resetPassword.emailSent'));
    } catch (error) {
      message.error(t('resetPassword.sendError'));
    } finally {
      setLoading(false);
    }
  };

  const handleFinishReset = async (values: { key: string; newPassword: string; confirmPassword: string }) => {
    if (values.newPassword !== values.confirmPassword) {
      message.error(t('resetPassword.passwordMismatch'));
      return;
    }

    try {
      setLoading(true);
      await authService.resetPasswordFinish(values.key, values.newPassword);
      message.success(t('resetPassword.resetSuccess'));
      handleCancel();
    } catch (error) {
      message.error(t('resetPassword.resetError'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setCurrentStep(0);
    setEmail('');
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title={t('resetPassword.title')}
      visible={visible}
      onCancel={handleCancel}
      footer={null}
      width={500}
    >
      <Steps current={currentStep} style={{ marginBottom: 24 }}>
        <Step title={t('resetPassword.stepEmail')} description={t('resetPassword.stepEmailDescription')} />
        <Step title={t('resetPassword.stepNewPassword')} description={t('resetPassword.stepCodeAndPassword')} />
      </Steps>

      {currentStep === 0 && (
        <Form
          form={form}
          layout="vertical"
          onFinish={handleInitReset}
        >
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            {t('resetPassword.instructionsSendEmail')}
          </Text>

          <Form.Item
            name="email"
            label={t('resetPassword.emailLabel')}
            rules={[
              { required: true, message: t('resetPassword.emailRequired') },
              { type: 'email', message: t('resetPassword.emailInvalid') }
            ]}
          >
            <Input
              prefix={<MailOutlined />}
              placeholder={t('resetPassword.emailPlaceholder')}
              size="large"
            />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              size="large"
              block
            >
              {t('resetPassword.sendResetLink')}
            </Button>
          </Form.Item>
        </Form>
      )}

      {currentStep === 1 && (
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinishReset}
        >
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            <Trans
              i18nKey="resetPassword.instructionsFinishReset"
              values={{ email }}
              components={{ strong: <strong /> }}
            />
          </Text>

          <Form.Item
            name="key"
            label={t('resetPassword.resetCodeLabel')}
            rules={[{ required: true, message: t('resetPassword.resetCodeRequired') }]}
          >
            <Input
              placeholder={t('resetPassword.resetCodePlaceholder')}
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="newPassword"
            label={t('resetPassword.newPasswordLabel')}
            rules={[
              { required: true, message: t('resetPassword.newPasswordRequired') },
              { min: 8, message: t('resetPassword.newPasswordMinLength') },
              {
                pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d\s])\S+$/,
                message: t('resetPassword.newPasswordPattern')
              }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder={t('resetPassword.newPasswordPlaceholder')}
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label={t('resetPassword.confirmPasswordLabel')}
            rules={[
              { required: true, message: t('resetPassword.confirmPasswordRequired') },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(t('resetPassword.confirmPasswordMismatch')));
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder={t('resetPassword.confirmPasswordPlaceholder')}
              size="large"
            />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              size="large"
              block
            >
              {t('resetPassword.resetPasswordButton')}
            </Button>
          </Form.Item>

          <Form.Item>
            <Button
              type="link"
              onClick={() => setCurrentStep(0)}
              block
            >
              {t('resetPassword.backToEmail')}
            </Button>
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
};

export default ResetPasswordModal;
